import { useCallback, useEffect, useMemo, useState } from 'react';

export interface UseBoolean {
  value: boolean;
  onTrue: () => void;
  onFalse: () => void;
  onToggle: () => void;
  setValue: (value: boolean | 'toggle') => void;
}

export function useBoolean(
  defaultValue: boolean = false,
  onValueChange?: (v: boolean) => void
): UseBoolean {
  const [value, setValueState] = useState(defaultValue);

  // Use useCallback to memoize setValue
  const setValue = useCallback(
    (v: boolean | 'toggle') => {
      setValueState((prev) => {
        const newValue = v === 'toggle' ? !prev : v;
        onValueChange?.(newValue);
        return newValue;
      });
    },
    [onValueChange]
  );

  useEffect(() => {
    setValue(defaultValue);
  }, [defaultValue]);

  const onTrue = useCallback(() => setValue(true), [setValue]);
  const onFalse = useCallback(() => setValue(false), [setValue]);
  const onToggle = useCallback(() => setValue('toggle'), [setValue]);

  return useMemo(
    () => ({
      value,
      onTrue,
      onFalse,
      onToggle,
      setValue,
    }),
    [value, onTrue, onFalse, onToggle, setValue]
  );
}
